/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import {
  Segment,
  Transition,
  Container,
  Dimmer,
  Loader,
  Button,
  Icon,
  Table,
  Grid,
  Modal,
  Dropdown,
  Accordion,
  DropdownProps,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import {
  authCheckCallApi,
  DateFormatLong,
} from "../../Authenticator/utils";
import {
  ListApiKeysQuery,
  GetApiKeyQuery,
  DestroyApiKeyMutation,
  ApiKey,
} from "../../../graphql/developer";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useTranslation } from "react-i18next";

interface DeveloperProps {
  sessionInfo: any;
}

const keyScopes: { [key: string]: any } = {
  aps: "Protection",
  "sail-config": "Monitoring for On-prem",
};

const Developer: React.FC<DeveloperProps> = () => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [showKeyId, setShowKeyId] = useState<string>("");
  const [showSecret, setShowSecret] = useState(false);
  const [showDeleteKey, setShowDeleteKey] = useState<string | boolean>(false);
  const [genKeyLoading, setGenKeyLoading] = useState(false);
  const [token, setToken] = useState<any>(null);
  const [selectedTokenScope, setSelectedTokenScope] = useState<string>("");
  const { t } = useTranslation(["developer", "common"]);
  const [
    listApiKeys,
    { loading: listKeysLoading, data: keysData, refetch: reListApiKeys },
  ] = useLazyQuery<any>(ListApiKeysQuery, {
    onError: () => {
      !message &&
        setMessage({
          title: t("list_keys"),
          message: t("fetch_error"),
        });
    },
  });

  const [deleteKey, { loading: deleteKeyLoading }] = useMutation(
    DestroyApiKeyMutation,
    {
      onError: () => {
        setMessage({
          title: t("delete_key"),
          message: t("delete_error"),
        });
      },
      onCompleted: () => {
        setMessage({
          title: t("delete_key"),
          message: t("delete_ok"),
        });
        reListApiKeys();
      },
    }
  );

  const [getKey, { loading: getKeyLoading, data: keyData }] = useLazyQuery<any>(
    GetApiKeyQuery,
    {
      onError: () => {
        if (!message) {
          setMessage({
            title: t("show"),
            message: t("show_error"),
          });
        }
      },
    }
  );

  useEffect(() => {
    authCheckCallApi(listApiKeys);
  }, [listApiKeys]);

  const getScopeList = (scope: string[]) => {
    const scopes: string[] = [];
    scope.forEach((scope) => {
      scopes.push(keyScopes[scope]);
    });
    return scopes.join(", ");
  };

  const getKeyRow = (key: ApiKey) => {
    return (
      <Table.Row
        style={{ cursor: "pointer" }}
        key={key.keyId}
        onClick={() => {
          setShowKeyId(key.keyId);
        }}
      >
        <Table.Cell>
          {new Date(key.created * 1000).toLocaleDateString(navigator.language)}
        </Table.Cell>
        <Table.Cell className={styles.monoSpaceText}>{key.keyId}</Table.Cell>
        <Table.Cell>{getScopeList(key.scopes)}</Table.Cell>
      </Table.Row>
    );
  };

  const copyText = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const getCopyButton = (element: string) => {
    return (
      <Icon
        name="copy"
        className={styles.copyButton}
        onClick={() => {
          switch (element) {
            case "key":
              return copyText(keyData.key.key);
            case "keyId":
              return copyText(showKeyId);
            default:
              return copyText(`Bearer ${token.token}`);
          }
        }}
      />
    );
  };

  const sortKeys = (updatedA: ApiKey, updatedB: ApiKey) => {
    if (updatedA.created < updatedB.created) {
      return 1;
    } else if (updatedA.created > updatedB.created) {
      return -1;
    }
    return 0;
  };

  const getKeysList = () => {
    if (keysData && keysData.keys.length) {
      const sortedKeys = keysData.keys.sort((keyA: ApiKey, keyB: ApiKey) =>
        sortKeys(keyA, keyB)
      );
      sortedKeys.reverse();

      return (
        <Table stackable selectable basic="very">
          <Table.Header>
            <Table.Row className={styles.invoiceTableHeader}>
              <Table.HeaderCell>{t("Created")}</Table.HeaderCell>
              <Table.HeaderCell>{t("id")}</Table.HeaderCell>
              <Table.HeaderCell>{t("scope")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedKeys.map((key: ApiKey) => getKeyRow(key))}
          </Table.Body>
        </Table>
      );
    }

    return (
      <Grid>
        <Grid.Column textAlign="center">
          <div className={styles.myAccountNoUsersLabel}>{t("no_keys")}</div>
        </Grid.Column>
      </Grid>
    );
  };

  const deleteApiKey = () => {
    authCheckCallApi(deleteKey, {
      variables: {
        keyId: showDeleteKey,
      },
    });
    setShowDeleteKey(false);
  };

  const generateToken = async (keyId: string, key: string) => {
    const credentials = window.btoa(`${keyId}:${key}`);

    const setGenerateError = (e?: string) => {
      const reason = e || "";
      setMessage({
        title: "Generate token",
        message: `Unable to generate the token at this time. ${reason}`,
      });
    };

    if (!process.env.REACT_APP_AWS_TOKEN_ENDPOINT) {
      setGenerateError();
      return;
    }

    setGenKeyLoading(true);
    await fetch(process.env.REACT_APP_AWS_TOKEN_ENDPOINT, {
      method: "POST",
      headers: new Headers({
        Authorization: "Basic " + credentials,
        "Content-Type": "application/json",
      }),
      body: `{ "scope": "${selectedTokenScope}" }`,
    })
      .then((response) => {
        if (!response.ok) {
          setGenerateError();
          return;
        }
        return response.json();
      })
      .then((json) => {
        if (!json) {
          setGenerateError();
          return;
        }
        setToken(json);
      })
      .catch((e) => {
        setGenerateError(e);
      })
      .finally(() => {
        setGenKeyLoading(false);
      });
  };

  const closeKeyPopup = () => {
    setShowKeyId("");
    setShowSecret(false);
    setToken(null);
    setSelectedTokenScope("");
  };

  const handleTokenScopeDropdownChange = (event: any, data: DropdownProps) => {
    setSelectedTokenScope(data.value as string);
  };

  const getTokenScopeOptions = (key: ApiKey) => {
    const scopes = key.scopes.map((scope) => {
      return { key: scope, value: scope, text: keyScopes[scope] };
    });

    return scopes;
  };

  const keyPopup = () => {
    return (
      <Transition transitionOnMount animation="fade" duration="250">
        <Modal
          closeOnDimmerClick={false}
          size="small"
          open
          onClose={closeKeyPopup}
        >
          <Modal.Content>
            <div
              className={styles.sectionHeaderMargin}
              style={{ display: "flex" }}
            >
              {t("key_details")}
              <span
                className={cStyles.closeButton}
                style={{ right: 20 }}
                onClick={closeKeyPopup}
              >
                <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
              </span>
            </div>
            <span style={{ marginRight: "1rem" }}>API Key ID:</span>
            <span className={styles.monoSpaceText}>{showKeyId}</span>
            <span style={{ marginLeft: "1rem" }}>{getCopyButton("keyId")}</span>
            <Accordion style={{ margin: "1rem" }}>
              <Accordion.Title
                active={showSecret === true}
                index={1}
                onClick={() => {
                  if (!showSecret) {
                    authCheckCallApi(getKey, {
                      variables: {
                        keyId: showKeyId,
                      },
                    });
                  }
                  setShowSecret(!showSecret);
                }}
              >
                <Ico
                  name={showSecret === true ? "down" : "right"}
                  className={styles.accordionIcon}
                />
                {showSecret ? "Hide Key" : "Show Key"}
              </Accordion.Title>
              <Accordion.Content active={showSecret === true}>
                <Dimmer active={getKeyLoading}>
                  <Loader active={getKeyLoading} size="medium" />
                </Dimmer>
                {!getKeyLoading && keyData && keyData.key && (
                  <span className={`${cStyles.input} ${styles.keyView}`}>
                    {keyData.key.key}
                    {getCopyButton("key")}
                  </span>
                )}

                {keyData && (
                  <div
                    className={styles.apiKeyGenerate}
                    style={{ textAlign: "start" }}
                  >
                    <span style={{ width: "60%" }}>
                      Token scope
                      <Dropdown
                        className={styles.myAccountInput}
                        name="tokenScope"
                        key="tokenScope"
                        disabled={!!token}
                        style={{ maxWidth: "60%", marginLeft: "1rem" }}
                        selection
                        options={getTokenScopeOptions(keyData.key)}
                        placeholder="Select token scope"
                        value={selectedTokenScope}
                        onChange={handleTokenScopeDropdownChange}
                        icon={
                          <Ico name="down" className={styles.dropdownIcon} />
                        }
                      />
                    </span>
                    <Button
                      disabled={!!token || !selectedTokenScope || genKeyLoading}
                      loading={genKeyLoading}
                      primary
                      className={cStyles.buttonPrime}
                      onClick={() => {
                        showKeyId && generateToken(showKeyId, keyData.key.key);
                      }}
                    >
                      <Ico name="settings" style={{ marginRight: "10px" }} />
                      {t("generate_token")}
                    </Button>
                  </div>
                )}

                {token && token.token && (
                  <React.Fragment>
                    <span className={`${cStyles.input} ${styles.keyView}`}>
                      {`Bearer ${token.token}`}
                      {getCopyButton("token")}
                    </span>

                    <div style={{ textAlign: "center" }}>
                      {t("token_expires")}{" "}
                      {new Date(token.expiry * 1000).toLocaleDateString(
                        navigator.language,
                        DateFormatLong
                      )}
                    </div>
                  </React.Fragment>
                )}
              </Accordion.Content>
            </Accordion>
          </Modal.Content>
          <div className={styles.apiKeyButtons}>
            <Button
              secondary
              className={cStyles.buttonSecond}
              onClick={() => {
                setShowDeleteKey(showKeyId);
              }}
            >
              <Ico name="trash" style={{ marginRight: "10px" }} />
              {t("common:remove")}
            </Button>

            <Button
              secondary
              className={cStyles.buttonSecond}
              onClick={closeKeyPopup}
            >
              <Ico name="cancel" style={{ marginRight: "10px" }} />
              {t("common:close")}
            </Button>
          </div>
        </Modal>
      </Transition>
    );
  };

  const dimmerActive = listKeysLoading ||  deleteKeyLoading;

  return (
    <React.Fragment>
      <Container id={styles.highlightsContainer}>
        <Transition transitionOnMount animation="fade in" duration="250">
          <Segment className={styles.myAccountSegment}>
            <Dimmer active={dimmerActive}>
              <Loader active={dimmerActive} size="large">
                {t("common:loading")}
              </Loader>
            </Dimmer>

            <div className={styles.contactsHeaderContainer}>
              <div className={styles.sectionHeader}>{t("api_keys")}</div>
            </div>
            {!listKeysLoading && getKeysList()}
          </Segment>
        </Transition>
      </Container>
      {showKeyId && keyPopup()}
      {showDeleteKey && (
        <ConfirmationDialog
          title={t("delete_key")}
          message={t("delete_confirm")}
          okTitle={t("delete")}
          cancelTitle={t("common:cancel")}
          onOk={() => {
            closeKeyPopup();
            deleteApiKey();
          }}
          onCancel={() => {
            setShowDeleteKey(false);
          }}
        />
      )}
      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Developer;
